import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';
import { AuthenticatedComponent } from './authenticated.component';
import { StaticRole } from '@markant-platform/shared/user/model';
import { CmsPageStaticComponent } from '../cms-page-static/cms-page-static.component';
import {
  AuthorizationRouteGuard,
  FeatureToggleRouteGuard,
  serviceManagerGuard,
} from '@markant-platform/shared/route-guard/feature';
import { ActiveConsentGuard } from '../guards/active-consent.guard';
import { AppConfigService, AppFeature } from '@markant-platform/shared/data-access';
import { FocusViewComponent } from '../focus-view/focus-view.component';
import { WidgetStore } from '@markant-platform/widgets/data-access';

const routes: Routes = [
  {
    path: '',
    component: AuthenticatedComponent,
    canActivate: [ActiveConsentGuard],
    providers: [WidgetStore],
    children: [
      {
        path: '',
        loadChildren: () =>
          import('@markant-platform/platform-shell/feature-dashboard').then(m => m.PlatformShellFeatureDashboardModule),
      },
      {
        path: 'cartridge',
        children: [
          {
            path: 'catalog',
            children: [
              {
                path: '',
                loadChildren: () =>
                  import('@markant-platform/cartridge/feature-catalog').then(m => m.CartridgeFeatureCatalogModule),
              },
              {
                path: ':cartridgeId/booking/:variantId',
                canActivate: [AuthorizationRouteGuard(StaticRole.ORGANIZATION_ADMIN)],
                loadChildren: () =>
                  import('@markant-platform/cartridge/feature-booking').then(m => m.CartridgeFeatureBookingModule),
              },
              {
                path: ':cartridgeId/upgrade/:variantId',
                canActivate: [AuthorizationRouteGuard(StaticRole.ORGANIZATION_ADMIN), serviceManagerGuard],
                loadComponent: () =>
                  import('@markant-platform/cartridge/feature-upgrade').then(m => m.FeatureUpgradeComponent),
              },
            ],
          },
          {
            path: 'management',
            loadChildren: () =>
              import('@markant-platform/cartridge/feature-management-view').then(
                m => m.CartridgeFeatureManagementViewModule
              ),
          },
          {
            path: 'bookingRequests',
            loadChildren: () =>
              import('@markant-platform/cartridge/feature-booking-requests').then(m => m.bookingRequestsRoutes),
          },
          {
            path: 'contactRequests',
            loadChildren: () =>
              import('@markant-platform/cartridge/feature-contact-requests').then(
                m => m.CartridgeFeatureContactRequestsModule
              ),
          },
        ],
      },
      {
        path: 'focus-view',
        children: [
          {
            path: '',
            component: FocusViewComponent,
          },
          {
            path: '**',
            redirectTo: '',
          },
        ],
      },
      {
        path: 'user-administration',
        data: {
          translationKey: 'userAccount',
        },
        loadChildren: () =>
          import('@markant-platform/user/feature-user-settings').then(m => m.UserFeatureUserSettingsModule),
      },
      {
        path: 'news',
        loadChildren: () =>
          import('@markant-platform/news-area/feature-news-dashboard').then(m => m.NewsAreaFeatureNewsDashboardModule),
      },
      {
        path: 'organization',
        canActivate: [AuthorizationRouteGuard(StaticRole.ORGANIZATION_ADMIN)],
        data: {
          translationKey: 'administration',
        },
        loadChildren: () =>
          import('@markant-platform/dashboard/feature-dashboard-overview').then(
            m => m.DashboardFeatureDashboardOverviewModule
          ),
      },
      {
        path: 'backoffice',
        canActivate: [AuthorizationRouteGuard(StaticRole.PLATFORM_ADMIN)],
        data: {
          translationKey: 'administration',
        },
        loadChildren: () =>
          import('@markant-platform/dashboard/feature-dashboard-overview').then(
            m => m.DashboardFeatureDashboardOverviewModule
          ),
      },
      {
        path: 'cartridge-admin',
        canActivate: [AuthorizationRouteGuard(StaticRole.LEISTUNG_ADMIN)],
        data: {
          translationKey: 'administration',
        },
        loadChildren: () =>
          import('@markant-platform/dashboard/feature-dashboard-overview').then(
            m => m.DashboardFeatureDashboardOverviewModule
          ),
      },
      {
        path: 'organization-contact',
        canActivate: [AuthorizationRouteGuard(StaticRole.ORGANIZATION_MEMBER)],
        loadComponent: () =>
          import('@markant-platform/organization/feature-organization-contact').then(
            m => m.FeatureOrganizationContactComponent
          ),
      },
      {
        path: 'enable-organization',
        loadChildren: () =>
          import('@markant-platform/platform-requests/feature-further-qualification').then(
            m => m.PlatformRequestsFeatureFurtherQualificationModule
          ),
      },
      {
        path: 'iam-toggle',
        canActivate: [FeatureToggleRouteGuard],
        data: {
          disabled: !AppConfigService.isFeatureEnabled(AppFeature.IAM_TOGGLE),
        },
        loadComponent: () => import('@markant-platform/shared/iam-toggle/feature').then(m => m.IamToggleComponent),
      },
      {
        path: 'legal-document/download',
        loadComponent: () =>
          import('@markant-platform/consent/feature-legal-document-download').then(
            m => m.LegalDocumentDownloadComponent
          ),
      },
      {
        path: 'legal-notice',
        component: CmsPageStaticComponent,
      },
      {
        path: 'privacy-notice',
        component: CmsPageStaticComponent,
      },
      {
        path: 'termsofuse',
        component: CmsPageStaticComponent,
      },
      {
        path: 'gtc',
        component: CmsPageStaticComponent,
      },
    ],
  },
];

@NgModule({
  imports: [RouterModule.forChild(routes)],
  exports: [RouterModule],
})
export class AuthenticatedRoutingModule {}
